/* =========================================================================
   UPGRADE FLOW (2.0, round 3): the motion and live-region layer for the
   Trade-in and Sell tools. Vanilla, no dependencies, additive.

   WHY IT EXISTS. assets/upgrade-tradein.js and assets/upgrade-sell.js (the
   md5-pinned money scripts) rebuild the WHOLE tool on every tap: render()
   clears the mount and draws it again. A CSS entrance on the step cards would
   therefore replay on every chip, and a CSS-only rail could not tell the
   station that just became done from the ones that already were. This file
   watches the mount, compares what is drawn now with what was drawn last
   time, and marks ONLY what is new:
     .ug-flow-enter    a step card that was not there before
     .ug-flow-done     a station that has just become done (its rail fills)
     .ug-flow-current  the station that has just become current
     .ug-flow-offer    the offer, when its figures are new or have changed
     .ug-flow-picked   the answer the customer has just pressed
   The look of each is in upgrade-tradein.css / upgrade-sell.css, inside
   prefers-reduced-motion: no-preference. A silent repaint (prices landing
   after the first paint) draws the same figures, so nothing is marked and
   nothing blinks.

   It also (1) holds aria-busy on the empty mount until the tool has drawn
   (the loading skeleton in the CSS pulses only while it is set), and (2)
   keeps one polite live region OUTSIDE the mount, because the mount is
   rebuilt on every answer and a live region inside it would be thrown away
   before it could speak. It announces the words the page already shows: the
   step count and the active step's heading. No figure, no new copy.

   After "Start again" the tool is redrawn from question 1 and the button that
   was pressed no longer exists, so focus would fall to <body> and a keyboard
   user would lose their place (2.0 round 3 repair 2). The first question's
   heading takes focus instead (tabindex -1, no ring: it is not a control), so
   the next Tab lands on the first answer.

   It reads the DOM and writes classes, one hidden status node and, after
   "Start again", the focus. It never touches a price, a rate, the deal state or
   a form. If it fails to load, the tool is exactly the static tool.
   ========================================================================= */
(function () {
  'use strict';
  if (window.__ugFlow) return;
  window.__ugFlow = true;

  var doc = document;
  var K = null;          // which tool is on this page
  var obs = null;        // the MutationObserver on its mount
  var prev = null;       // what was drawn last time
  var pick = null;       // the answer pressed since the last draw
  var again = false;     // "Start again" was pressed: the next draw is question 1
  var live = null;       // the hidden status node
  var stall = 0;

  function kind() {
    var m = doc.querySelector('.ug-engine');
    if (m) return { mount: m, step: '.ug-step', item: '.ug-progress__item', count: '.ug-progress__count', title: '.ug-step__title', offer: '.ug-offers', fig: '.ug-offer__v' };
    m = doc.querySelector('.ug-sell-engine');
    if (m) return { mount: m, step: '.ug-sell-step', item: '.ug-sell-progress__item', count: '.ug-sell-progress__count', title: '.ug-sell-step__title', offer: '.ug-sell-offer', fig: '.ug-sell-offer__v' };
    return null;
  }

  function all(sel, root) { return Array.prototype.slice.call((root || K.mount).querySelectorAll(sel)); }
  function txt(el) { return el ? String(el.textContent || '').replace(/\s+/g, ' ').trim() : ''; }

  /* what is on screen, as numbers and one signature of the offer's figures */
  function snap() {
    var items = all(K.item);
    var cur = 0, done = 0;
    items.forEach(function (li, i) {
      if (li.classList.contains('is-done')) done = i + 1;
      if (li.classList.contains('is-current')) cur = i + 1;
    });
    var sig = all(K.fig).map(txt).join('|');
    return { n: all(K.step).length, cur: cur, done: done, sig: sig };
  }

  function say(s) {
    if (!live) {
      live = doc.createElement('div');
      live.className = 'ug-visually-hidden';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      K.mount.parentNode.insertBefore(live, K.mount);
    }
    var steps = all(K.step);
    var last = steps[steps.length - 1];
    var sep = (doc.documentElement.getAttribute('dir') === 'rtl') ? '، ' : ', ';
    var line = [txt(K.mount.querySelector(K.count)), txt(last && last.querySelector(K.title))].filter(Boolean).join(sep);
    if (line) live.textContent = line;
  }

  /* The tool draws its step titles as <h3> under the page's <h1>, and its "Trade up to"
     as <h4>: the outline skips a level (axe: heading-order). The scripts that draw
     them cannot be edited, so the levels are set here, as ARIA, after every draw:
     steps are level 2 (the page's h1 > h2), the rail's heading level 3. */
  function outline() {
    all(K.title).forEach(function (h) { h.setAttribute('aria-level', '2'); });
    all('.ug-subhead').forEach(function (h) { h.setAttribute('aria-level', '3'); });
  }

  function onDraw() {
    if (!K.mount.childNodes.length) return;      // cleared, not yet redrawn
    var s = snap();
    window.clearTimeout(stall);
    K.mount.removeAttribute('aria-busy');
    outline();

    if (!prev) {                                  // the first draw: the skeleton gives way, once
      K.mount.setAttribute('data-ug-flow', 'first');
      window.setTimeout(function () { K.mount.removeAttribute('data-ug-flow'); }, 900);
      prev = s;
      return;
    }

    var steps = all(K.step), items = all(K.item), i;
    for (i = prev.n; i < s.n; i++) if (steps[i]) steps[i].classList.add('ug-flow-enter');
    for (i = prev.done; i < s.done; i++) if (items[i]) items[i].classList.add('ug-flow-done');
    if (s.cur > prev.cur && items[s.cur - 1]) items[s.cur - 1].classList.add('ug-flow-current');

    if (s.sig && s.sig !== prev.sig) {
      var offer = K.mount.querySelector(K.offer);
      if (offer) offer.classList.add('ug-flow-offer');
    }

    if (pick) {
      var scope = steps[pick.step] || K.mount;
      all('button', scope).some(function (b) {
        if (String(b.className).split(' ')[0] === pick.cls && b.textContent === pick.text) { b.classList.add('ug-flow-picked'); return true; }
        return false;
      });
      pick = null;
    }

    if (again) {                                  // "Start again": keep the keyboard user's place
      again = false;
      var first = steps[0] && steps[0].querySelector(K.title);
      if (first) {
        first.setAttribute('tabindex', '-1');
        try { first.focus(); } catch (err) { /* nothing to focus */ }
      }
    }

    if (s.n !== prev.n || s.cur !== prev.cur) say(s);
    prev = s;
  }

  function onClick(e) {
    var b = e.target && e.target.closest ? e.target.closest('button') : null;
    if (!b || !K.mount.contains(b)) return;
    again = /(^|\s)(ug-reset|ug-sell-reset)(\s|$)/.test(String(b.className));
    var st = b.closest(K.step);
    pick = { cls: String(b.className).split(' ')[0], text: b.textContent, step: st ? all(K.step).indexOf(st) : -1 };
  }

  function start() {
    K = kind();
    if (!K) return;
    prev = null; pick = null; again = false; live = null;
    if (obs) obs.disconnect();
    if (!K.mount.childNodes.length) {
      K.mount.setAttribute('aria-busy', 'true');
      // a tool that never draws must not leave the region "busy" for a screen reader
      stall = window.setTimeout(function () { K.mount.removeAttribute('aria-busy'); K.mount.setAttribute('data-ug-flow', 'stalled'); }, 15000);
    } else {
      prev = snap();                              // the tool drew before this script ran: no entrance
    }
    obs = new MutationObserver(onDraw);
    obs.observe(K.mount, { childList: true });
    K.mount.addEventListener('click', onClick, true);
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start); else start();
  // the theme editor re-renders the section; the tool script re-runs against the new mount
  doc.addEventListener('shopify:section:load', function () { var k = kind(); if (k && (!K || k.mount !== K.mount)) start(); });
})();
